import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertTriangle, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { api } from "@/lib/api";
import { buildWhatsappUrl, CLINIC_WHATSAPP } from "@/lib/clinic";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isRazorpay = params.get("rzp") === "1";
  const rzpApptId = params.get("appointment_id");
  const [status, setStatus] = useState(isRazorpay ? "paid" : "checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (isRazorpay) return; // Razorpay is already verified server-side, skip polling
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attemptsRef.current >= 8) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        console.warn(`Payment status polling error (attempt ${attemptsRef.current}):`, err);
        if (attemptsRef.current >= 5) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, isRazorpay]);

  // Once paid, fetch appointment details so we can build a WhatsApp confirmation
  useEffect(() => {
    if (status !== "paid") return;
    let apptId = rzpApptId || null;
    if (!apptId) {
      try {
        apptId = localStorage.getItem("last_appointment_id");
      } catch (storageErr) {
        console.warn("Could not read last_appointment_id:", storageErr);
      }
    }
    if (!apptId) return;
    api.get(`/appointments/lookup/${apptId}`)
      .then((r) => setAppointment(r.data))
      .catch((err) => {
        console.error("Failed to load appointment for WhatsApp confirmation:", err);
      });
  }, [status, rzpApptId]);

  return (
    <div className="min-h-screen bg-clinic-bg text-clinic-ink pt-28 pb-20">
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        <div className="card-elevated p-10 md:p-12 text-center" data-testid="payment-status-card">
          {status === "checking" && (
            <>
              <Loader2 className="animate-spin mx-auto text-clinic-pine" size={36} />
              <h2 className="font-heading text-3xl mt-6 tracking-tight">Confirming payment...</h2>
              <p className="text-clinic-muted mt-2">Just a moment while we verify your payment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 rounded-full bg-clinic-sage/60 grid place-items-center mx-auto text-clinic-pine">
                <Check size={32} />
              </div>
              <h2 className="font-heading text-3xl mt-6 tracking-tight">Payment received</h2>
              <p className="text-clinic-muted mt-2 max-w-md mx-auto">
                Your appointment is confirmed. Tap the button below to share confirmation on WhatsApp.
              </p>
              {details && (
                <div className="mt-6 inline-flex items-baseline gap-2 text-clinic-ink">
                  <span className="font-heading text-3xl">
                    ₹{(details.amount_total/100).toLocaleString("en-IN", {maximumFractionDigits: 2})}
                  </span>
                  <span className="text-sm uppercase text-clinic-muted">paid</span>
                </div>
              )}
              {!details && appointment?.total_amount != null && (
                <div className="mt-6 inline-flex items-baseline gap-2 text-clinic-ink">
                  <span className="font-heading text-3xl">
                    ₹{Number(appointment.total_amount).toLocaleString("en-IN", {maximumFractionDigits: 2})}
                  </span>
                  <span className="text-sm uppercase text-clinic-muted">paid</span>
                </div>
              )}
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {appointment ? (
                  <a
                    data-testid="success-whatsapp-btn"
                    href={buildWhatsappUrl({ ...appointment, payment_status: "paid" })}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB854] text-white font-semibold rounded-[14px] px-7 py-4 transition shadow-lg shadow-[#25D366]/30"
                  >
                    <FaWhatsapp size={20} /> Send confirmation on WhatsApp
                  </a>
                ) : (
                  <a href={`https://wa.me/${CLINIC_WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="success-fallback-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB854] text-white font-semibold rounded-[14px] px-7 py-4 transition">
                    <FaWhatsapp size={20} /> WhatsApp us
                  </a>
                )}
                <Link to="/" data-testid="success-home-btn" className="btn-ghost">Back to home</Link>
              </div>
            </>
          )}
          {(status === "failed" || status === "expired") && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 grid place-items-center mx-auto text-red-500">
                <AlertTriangle size={32} />
              </div>
              <h2 className="font-heading text-3xl mt-6 tracking-tight">
                {status === "expired" ? "Session expired" : "Payment not confirmed"}
              </h2>
              <p className="text-clinic-muted mt-2">No charge was made. You can try again or pay at the clinic.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/book" data-testid="retry-book-btn" className="btn-primary">Try again</Link>
                <Link to="/" className="btn-ghost">Home</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
